const clampChannel = (value) => Math.min(255, Math.max(0, value));

const normalizeHex = (input) => {
  if (typeof input !== 'string') return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withoutHash = trimmed.startsWith('#') ? trimmed.slice(1) : trimmed;
  if (/^[0-9a-fA-F]{3}$/.test(withoutHash)) {
    return withoutHash
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (/^[0-9a-fA-F]{6}$/.test(withoutHash)) {
    return withoutHash;
  }
  return null;
};

const parseRgbLike = (input) => {
  if (typeof input !== 'string') return null;
  const match = input.match(/rgba?\(([^)]+)\)/i);
  if (!match) return null;
  const parts = match[1].split(',').map((part) => part.trim());
  if (parts.length < 3) return null;
  const [r, g, b] = parts;
  const parsed = [Number(r), Number(g), Number(b)];
  if (parsed.some((channel) => Number.isNaN(channel))) return null;
  return parsed.map((channel) => clampChannel(channel));
};

const hexToRgb = (hex) => {
  const normalizedHex = normalizeHex(hex);
  if (!normalizedHex) return null;
  return [
    parseInt(normalizedHex.slice(0, 2), 16),
    parseInt(normalizedHex.slice(2, 4), 16),
    parseInt(normalizedHex.slice(4, 6), 16),
  ];
};

const parseColorToRgb = (color) => hexToRgb(color) || parseRgbLike(color);

const toLinear = (channel) => {
  const value = channel / 255;
  return value <= 0.04045
    ? value / 12.92
    : Math.pow((value + 0.055) / 1.055, 2.4);
};

export const getRelativeLuminance = (color) => {
  const rgb = parseColorToRgb(color);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const getContrastRatio = (l1, l2) => {
  const brighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (brighter + 0.05) / (darker + 0.05);
};

export const getContrastTextColor = (backgroundColor, darkText = '#111111', lightText = '#FFFFFF') => {
  const luminance = getRelativeLuminance(backgroundColor);
  if (luminance === null) return lightText;
  const contrastWithDark = getContrastRatio(luminance, 0);
  const contrastWithLight = getContrastRatio(luminance, 1);
  return contrastWithDark >= contrastWithLight ? darkText : lightText;
};

export const isLightColor = (color) => {
  const luminance = getRelativeLuminance(color);
  if (luminance === null) return false;
  return luminance > 0.5;
};
